import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, MessageSquare, ChevronRight } from 'lucide-react';
import './ContactUs.css';

export function ContactUs() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');

    // Simulate sending brief to our support email
    setTimeout(() => {
      setStatus('success');
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
      // Auto-clear success message after 5 seconds
      setTimeout(() => setStatus('idle'), 5000);
    }, 1800);
  };

  return (
    <div className="contact-page">
      {/* Ambient background */}
      <div className="contact-bg">
        <div className="contact-bg__orb contact-bg__orb--1" />
        <div className="contact-bg__orb contact-bg__orb--2" />
        <div className="contact-bg__grid" />
      </div>

      <div className="contact-container">
        {/* Breadcrumbs */}
        <div className="contact-breadcrumbs">
          <Link to="/">Home</Link>
          <ChevronRight size={12} />
          <span>Contact Us</span>
        </div>

        {/* Header */}
        <header className="contact-header">
          <span className="contact-eyebrow">Connect With Us</span>
          <h1 className="contact-title">Let's Staged the <span className="contact-title__accent">Inevitable</span></h1>
          <p className="contact-subtitle">
            Have a ticketing query, need booking assistance, or want to discuss a private spectacle brief? Reach our team below.
          </p>
        </header>

        {/* Form and info grid */}
        <div className="contact-layout">
          {/* Contact Details Column */}
          <div className="contact-info">
            <div className="contact-info__card">
              <h3>Primary Contacts</h3>
              
              <div className="contact-info__row">
                <div className="contact-info__icon"><Mail size={18} /></div>
                <div>
                  <span className="contact-info__label">General Support Desk</span>
                  <a href="mailto:luminarentertainments123@gmail.com" className="contact-info__value">
                    luminarentertainments123@gmail.com
                  </a>
                </div>
              </div>

              <div className="contact-info__row">
                <div className="contact-info__icon"><Phone size={18} /></div>
                <div>
                  <span className="contact-info__label">Phone Hotline</span>
                  <a href="tel:+918129077431" className="contact-info__value">
                    +91 81290 77431
                  </a>
                </div>
              </div>

              <div className="contact-info__row">
                <div className="contact-info__icon"><MapPin size={18} /></div>
                <div>
                  <span className="contact-info__label">Luminar entertainments private limited</span>
                  <span className="contact-info__value" style={{ fontSize: '0.9rem', lineHeight: '1.4' }}>
                    3rd Floor, Royal Plaza, Bank junction, Periyar Nagar, Aluva, Kerala 683101
                  </span>
                </div>
              </div>
            </div>

            <div className="contact-info__note">
              <div className="contact-info__note-icon"><MessageSquare size={16} /></div>
              <div>
                <strong>Ticketing Support:</strong>
                <p>Please include your unique booking Reference Code (e.g. <code>ORDER-XXXXX</code>) when mailing us regarding completed ticket orders to speed up our lookups.</p>
              </div>
            </div>
          </div>

          {/* Form Column */}
          <div className="contact-form-wrap">
            <form onSubmit={handleSubmit} className="contact-form">
              <h3>Send a Message</h3>

              {status === 'success' && (
                <div className="contact-alert contact-alert--success">
                  ✨ <strong>Successfully sent</strong>
                </div>
              )}

              {status === 'error' && (
                <div className="contact-alert contact-alert--error">
                  ❌ <strong>Something went wrong.</strong> Please check your connection and retry.
                </div>
              )}

              <div className="contact-form__field">
                <label htmlFor="contact-name">Full Name *</label>
                <input
                  id="contact-name"
                  type="text"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  disabled={status === 'sending'}
                />
              </div>

              <div className="contact-form__field">
                <label htmlFor="contact-email">Email Address *</label>
                <input
                  id="contact-email"
                  type="email"
                  placeholder="john@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={status === 'sending'}
                />
              </div>

              <div className="contact-form__field">
                <label htmlFor="contact-subject">Subject *</label>
                <input
                  id="contact-subject"
                  type="text"
                  placeholder="Ticketing / Rescheduling Enquiry"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                  disabled={status === 'sending'}
                />
              </div>

              <div className="contact-form__field">
                <label htmlFor="contact-message">Message *</label>
                <textarea
                  id="contact-message"
                  placeholder="Tell us details of your inquiry..."
                  rows={5}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  disabled={status === 'sending'}
                />
              </div>

              <button
                type="submit"
                className="contact-submit-btn"
                disabled={status === 'sending'}
              >
                {status === 'sending' ? (
                  <><span className="contact-spinner" /> Dispatching...</>
                ) : (
                  <>
                    <span>Send Message</span>
                    <Send size={16} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
